import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteRequest, getRequest, postRequest, updateRequest } from "../services";
import toast from "react-hot-toast";

const initialState = {
  incidentReports: [],
  loading: false,
  error: null,
};

export const fetchIncidentReports = createAsyncThunk(
  "incidentReports/fetchIncidentReports",
  async (studentId = null, { rejectWithValue }) => {
    try {
      const url = studentId
        ? `/api/IncidentReport?studentId=${studentId}`
        : "/api/IncidentReport";
      const response = await getRequest(url);
      if (response.status !== 200) {
        return rejectWithValue("Failed to fetch incident reports");
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createIncidentReport = createAsyncThunk(
  "incidentReports/createIncidentReport",
  async (incidentData, { rejectWithValue }) => {
    try {
      const response = await postRequest("/api/IncidentReport", incidentData);
      if (response.status !== 200) {
        return rejectWithValue("Failed to create incident report");
      }
      toast.success("Incident report created successfully!");
      return response.data?.data;
    } catch (error) {
      toast.error("Error in creating incident report!");
      return rejectWithValue(error.message);
    }
  }
);

export const updateIncidentReport = createAsyncThunk(
  "incidentReports/updateIncidentReport",
  async ({ id, incidentData }, { rejectWithValue }) => {
    try {
      const response = await updateRequest(`/api/IncidentReport/${id}`, incidentData);
      if (response.status !== 200) {
        return rejectWithValue("Failed to update incident report");
      }
      toast.success("Incident report updated successfully!");
      return response.data?.data;
    } catch (error) {
      toast.error("Error in updating incident report!");
      return rejectWithValue(error.message);
    }
  }
);

export const deleteIncidentReport = createAsyncThunk(
  "incidentReports/deleteIncidentReport",
  async (id, { rejectWithValue }) => {
    try {
      await deleteRequest(`/api/IncidentReport/${id}`);
      toast.success("Incident report deleted successfully!");
      return id;
    } catch (error) {
      toast.error("Error in deleting incident report!");
      return rejectWithValue(error.message);
    }
  }
);

const incidentReportSlice = createSlice({
  name: "incidentReports",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchIncidentReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIncidentReports.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data?.items || action.payload?.data || action.payload || [];
        state.incidentReports = Array.isArray(data) ? data : [];
      })
      .addCase(fetchIncidentReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch incident reports";
        toast.error(state.error);
      })
      .addCase(createIncidentReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createIncidentReport.fulfilled, (state, action) => {
        state.loading = false;
        state.incidentReports = [...state.incidentReports, action.payload];
      })
      .addCase(createIncidentReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create incident report";
      })
      .addCase(updateIncidentReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateIncidentReport.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.incidentReports.findIndex((report) => report.id === action.payload.id);
        if (index !== -1) state.incidentReports[index] = action.payload;
      })
      .addCase(updateIncidentReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update incident report";
      })
      .addCase(deleteIncidentReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteIncidentReport.fulfilled, (state, action) => {
        state.loading = false;
        state.incidentReports = state.incidentReports.filter((report) => report.id !== action.payload);
      })
      .addCase(deleteIncidentReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete incident report";
      });
  },
});

export default incidentReportSlice.reducer;